import type { MarketConfigDTO } from '../../features/settings/types/settings.types';
import { RichTaskTextEditor } from '../../features/tasks/components/RichTaskTextEditor';
import { WelfarePopupBlocksEditor, WelfarePopupBlocksPreview } from './WelfarePopupBlocksEditor';
import { useEffect, useState } from 'react';
import type { WelfarePopupConfigDTO, WelfarePopupUpdateParams } from '../../features/settings/types/welfare-popup.types';
import { formatDateTime } from '../../lib/format';
import { normalizeImageValueForSubmit, uploadImageToCos, resolveImagePreviewUrl } from '../../lib/upload/cos';

const emptyForm: WelfarePopupUpdateParams = {
  enabled: false,
  market_id: 'CN',
  title: '',
  content: '',
  image_url: '',
  button_text: '立即查看',
  blocks: [],
};

interface Props {
  data: WelfarePopupConfigDTO | null;
  saving: boolean;
  markets: MarketConfigDTO[];
  onUpdate: (params: WelfarePopupUpdateParams) => Promise<boolean>;
}

function pickPopupForm(data: WelfarePopupConfigDTO | null, marketId: string): WelfarePopupUpdateParams {
  const item = data?.items?.find((entry) => (entry.market_id || 'CN') === marketId);
  if (item) {
    return {
      enabled: item.enabled,
      market_id: item.market_id || marketId,
      title: item.title ?? '',
      content: item.content ?? '',
      blocks: item.blocks ?? [],
      image_url: item.image_url ?? '',
      button_text: item.button_text || '立即查看',
    };
  }
  if (data && (data.market_id || 'CN') === marketId) {
    return {
      enabled: data.enabled,
      market_id: data.market_id || marketId,
      title: data.title ?? '',
      content: data.content ?? '',
      blocks: data.blocks ?? [],
      image_url: data.image_url ?? '',
      button_text: data.button_text || '立即查看',
    };
  }
  return { ...emptyForm, market_id: marketId };
}

export function WelfarePopupSection({ data, saving, markets, onUpdate }: Props) {
  const defaultMarketId = markets[0]?.market_id || 'CN';
  const [selectedMarketId, setSelectedMarketId] = useState(defaultMarketId);
  const [form, setForm] = useState<WelfarePopupUpdateParams>(emptyForm);
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [blocksUploading, setBlocksUploading] = useState(false);
  const [error, setError] = useState('');
  const busy = saving || uploading || blocksUploading;

  useEffect(() => {
    if (markets.length === 0) return;
    if (!markets.some((market) => market.market_id === selectedMarketId)) {
      setSelectedMarketId(defaultMarketId);
    }
  }, [defaultMarketId, markets, selectedMarketId]);

  useEffect(() => {
    setForm(pickPopupForm(data, selectedMarketId));
    setUploadedImageUrl('');
  }, [data, selectedMarketId]);

  const handleMarketChange = (marketId: string) => {
    setSelectedMarketId(marketId);
    setForm(pickPopupForm(data, marketId));
    setUploadedImageUrl('');
  };

  const save = async () => {
    setError('');
    if (form.enabled && !form.content.trim() && !form.image_url.trim() && !form.blocks?.length) {
      setError('启用弹窗时，请至少填写文字、图片或一个富媒体内容块。'); return;
    }
    if (form.blocks?.some(block => block.type === 'text' ? !block.text?.trim() : !block.url?.trim() || ((block.type === 'video' || block.type === 'audio') && !/^https:\/\//i.test(block.url)))) {
      setError('请补全每个内容块；视频和音频须填写 HTTPS 媒体直链。'); return;
    }
    const ok = await onUpdate({
      ...form,
      market_id: selectedMarketId,
      blocks: (form.blocks ?? []).map(block => block.type === 'image' ? { ...block, url: normalizeImageValueForSubmit(block.url ?? '') } : block),
      image_url: normalizeImageValueForSubmit(form.image_url ?? ''),
    });
    if (!ok || !data) return;
  };

  const handleImageChange = async (file: File | null) => {
    if (!file) return;
    try {
      setUploading(true);
      setError('');
      const result = await uploadImageToCos(file, 'home-popup');
      setUploadedImageUrl(result.readUrl);
      setForm((current) => ({ ...current, image_url: result.relativePath }));
    } catch (e) {
      setError(e instanceof Error ? e.message : '图片上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">福利活动弹窗</h2>
          <p className="mt-1 text-sm text-gray-500">
            启用后，用户每天首次进入首页时展示，当天不再重复。点击图片或“立即查看”进入 App 内福利专区；点击关闭可继续使用。
          </p>
        </div>
        {data?.updated_at ? (
          <span className="text-xs text-gray-400">
            更新于 {formatDateTime(data.updated_at)}
          </span>
        ) : null}
      </div>

      <fieldset disabled={busy} className="min-w-0 rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-4 max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            所属地区 <span className="text-red-500">*</span>
          </label>
          <select
            value={selectedMarketId}
            onChange={(event) => handleMarketChange(event.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {markets.map((market) => (
              <option key={market.market_id} value={market.market_id}>
                {market.market_id} · {market.name}
              </option>
            ))}
          </select>
        </div>

        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用弹窗
        </label>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <input
            type="text"
            value={form.title}
            onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            aria-label="弹窗标题"
            maxLength={200}
            placeholder="弹窗标题（选填）"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <input
            type="text"
            value={form.button_text}
            onChange={(event) => setForm((current) => ({ ...current, button_text: event.target.value }))}
            aria-label="按钮文字"
            maxLength={40}
            placeholder="按钮文字（默认立即查看）"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <div className="mt-3">
          <p className="mb-2 text-sm font-medium text-gray-700">文字内容（选填）</p>
          <RichTaskTextEditor value={form.content} onChange={content => setForm(current => ({ ...current, content }))} disabled={busy} minRows={4} placeholder="支持加粗、红色和大字，可与图片及富媒体组合" />
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
          <input
            type="text"
            value={form.image_url}
            onChange={(event) => setForm((current) => ({ ...current, image_url: event.target.value }))}
            aria-label="封面图片地址"
            placeholder="封面图片 URL 或上传后自动生成"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <label className="flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            上传图片
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(event) => void handleImageChange(event.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        {uploading ? <p className="text-xs text-gray-400">图片上传中...</p> : null}

        {form.image_url ? (
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
            <img
              src={resolveImagePreviewUrl(form.image_url, form.image_url === data?.image_url ? undefined : { [form.image_url]: uploadedImageUrl })}
              alt="弹窗预览"
              className="max-h-64 w-full object-contain"
            />
          </div>
        ) : null}

        <WelfarePopupBlocksEditor key={selectedMarketId} value={form.blocks ?? []} onChange={blocks => setForm(current => ({ ...current, blocks }))} disabled={busy} onUploading={setBlocksUploading} />
        <details className="mt-4 rounded border border-gray-200 p-3">
          <summary className="cursor-pointer text-sm font-medium">内容预览</summary>
          <div className="mx-auto mt-3 max-h-96 max-w-sm space-y-3 overflow-auto p-3">
            {form.image_url && <img src={resolveImagePreviewUrl(form.image_url, { [form.image_url]: uploadedImageUrl })} alt="封面" className="max-h-64 w-full object-contain" />}
            {form.title && <h3 className="break-words text-center text-lg font-semibold">{form.title}</h3>}
            <WelfarePopupBlocksPreview blocks={[...(form.content ? [{ type: 'text' as const, text: form.content }] : []), ...(form.blocks ?? [])]} />
            <p className="rounded bg-primary p-3 text-center text-sm text-white">{form.button_text || '立即查看'} → 福利专区</p>
          </div>
        </details>
        {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            保存弹窗
          </button>
          {form.image_url ? (
            <button
              type="button"
              onClick={() => setForm((current) => ({ ...current, image_url: '' }))}
              className="h-9 px-3 text-sm text-gray-500 transition-colors hover:text-gray-700"
            >
              移除图片
            </button>
          ) : null}
        </div>
      </fieldset>
    </section>
  );
}
